<template>
  <div>
    <div v-if="item.response">
      <DrawablePhotoV1OrV2
        v-for="(photo, index) in responses"
        :key="photo.uuid"
        photo-class="mr-6 mb-3"
        :image="photo"
        :drawing="photo.drawing"
        :thumbnail="photo.thumbnail"
        :width="photo.width"
        :height="photo.height"
        :loading="photo.loading"
        :allow-drawing="allowDrawing"
        @change="updateDrawing(index, arguments[0])"
        @delete="deletePhoto(index)"
      />
    </div>

    <div class="mt-2">
      <label v-if="canAddPhoto" class="cursor-pointer">
        <input
          ref="fileInput"
          class="hidden"
          type="file"
          :accept="allowedImageTypesInputTag"
          :disabled="loading"
          :multiple="item.params.allow_multiple"
          @change="addPhoto"
        >
        <span
          class="btn btn-default btn-primary main-button"
          :disabled="loading"
        >
                    <Loader v-if="loading" />
                    <template v-else>
                        <CameraIcon width="24" height="24" class="mr-2 -mt-1" />
                        <span>{{ addPhotoButtonTitle }}</span>
                    </template>
                </span>
      </label>
      <div v-else-if="!allowGallerySelection" class="message is-info">
        <p class="message-body">
        {{ $t("inspections.gallery-restricted-message") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
  /* eslint-disable no-plusplus, no-await-in-loop, no-continue, func-names */
  import * as R from 'ramda';
  import promisePool from '@rodrigogs/promise-pool';
  import { v4 as uuidv4 } from 'uuid';
  import PhotoReader from '@/_legacy/util/PhotoReader';
  import Loader from '@/_legacy/components/Icons/Loader.vue';
  import CameraIcon from '@/_legacy/components/Icons/CameraIcon.vue';
  import { eventBus } from '@/_legacy/eventBus';
  import { DrawablePhotoV1OrV2 } from '@/_legacy/components/DrawablePhoto/DrawablePhotoV1OrV2';
  import { getPhotoStub } from '@/services/assets-api/AssetsPhotoService';
  import { allowedImageTypesInputTag } from '@/services/assets-api/assetsValidation';

  const createStubResponse = filesLength => {
    const photoStub = getPhotoStub();
    return Array(filesLength)
      .fill()
      .map(() => ({ uuid: uuidv4(), ...photoStub }));
  };

  const iterateThroughFiles = function* (files, stubs) {
    for (let i = 0; i < files.length; i++) {
      const photoStub = stubs[i];
      if (!photoStub) {
        continue;
      }

      yield {
        file: files[i],
        stub: stubs[i],
      };
    }
  };

  const processFile = async (file, stub, responses, { $t }) => {
    try {
      const {
        photo,
        thumbnail,
        width,
        height,
      } = await PhotoReader.read(
        file,
      );

      return responses.map(item => {
        if (!item.uuid || item.uuid !== stub.uuid) {
          return item;
        }

        return {
          uuid: uuidv4(),
          thumbnail,
          image: photo,
          width,
          height,
          drawing: null,
        };
      });
    } catch (error) {
      const errorMessage = $t('Error loading image: {error}', { error });
      eventBus.$emit('error', errorMessage);
      return responses.filter(item => {
        return !item.uuid || item.uuid !== stub.uuid;
      });
    }
  };

  // Note: it has photo uploading, but seems like used only in Preview,
  // means uploaded photo not saved, no need to upload it to assets
  export default {
    name: 'ConductInspectionItemQuestionPhoto',
    components: {
      DrawablePhotoV1OrV2,
      CameraIcon,
      Loader,
    },
    props: {
      item: {
        type: Object,
        required: true,
      },
    },
    data: () => ({
      loading: false,
      allowedImageTypesInputTag,
    }),
    computed: {
      responses() {
        return this.item.response.map(item => ({
          uuid: item.uuid || uuidv4(),
          ...item,
        }));
      },
      addPhotoButtonTitle() {
        if (this.item.response && this.item.response) {
          return this.$t('Take another photo');
        }
        return this.$t('Take photo');
      },
      canAddPhoto() {
        if (!this.allowGallerySelection) {
          return false;
        }

        const isEmptyResponse = !this.item.response || this.item.response < 1;
        return !!(this.item.params.allow_multiple || isEmptyResponse) && this.allowGallerySelection;
      },
      allowDrawing() {
        return R.pathOr(true, ['params', 'allow_drawing'], this.item);
      },
      allowGallerySelection() {
        return R.pathOr(true, ['params', 'allow_gallery_selection'], this.item);
      },
    },
    methods: {
      deletePhoto(indexToDelete) {
        const response = this.item.response.filter(
          (_item, index) => index !== indexToDelete,
        );
        this.$emit('change', {
          uuid: this.item.uuid,
          response,
          responded: !!response.length,
        });
      },
      updateDrawing(indexToUpdate, drawing) {
        const response = this.item.response.map((item, index) => (index !== indexToUpdate ? item : {
          ...item,
          drawing,
        }));
        this.$emit('change', {
          uuid: this.item.uuid,
          response,
          responded: !!response.length,
        });
      },
      async addPhoto(event) {
        if (!event.target.files) {
          return;
        }

        const itemResponse = this.item.response || [];
        const stubs = createStubResponse(event.target.files.length);
        this.loading = true;

        this.$emit('change', {
          uuid: this.item.uuid,
          response: [...itemResponse, ...stubs],
          responded: true,
        });

        await promisePool({
          concurrency: 1,
          generator: iterateThroughFiles(event.target.files, stubs),
          processor: async ({
            file,
            stub,
          }) => {
            const response = await processFile(
              file,
              stub,
              this.item.response,
              { $t: this.$t },
            );
            this.$emit('change', {
              uuid: this.item.uuid,
              response,
              responded: !!response.length,
            });
          },
        });

        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = '';
        }

        this.loading = false;
      },
    },
  };
</script>
